"use client";
import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import Select from "react-select";

export const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
const ACCENT = "#f97316";

export const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
export const cls = (...c) => c.filter(Boolean).join(" ");

export const STATUS_STYLE = {
  issued: "bg-yellow-700/60 text-yellow-100",
  completed: "bg-green-700/60 text-green-100",
  paid: "bg-blue-700/60 text-blue-100",
};
export const STATUS_LABEL = {
  issued: "ISSUED · INCURRED",
  completed: "COMPLETED · COMMITTED",
  paid: "PAID",
};

export const projectLabel = (a) => {
  const p = a?.project;
  if (!p || typeof p !== "object") return "—";
  return p.name || p.projectName || p.title || "—";
};

export const woLabel = (a) => {
  const w = a?.workOrder3;
  if (!w || typeof w !== "object") return "—";
  return [w.woNumber, w.title].filter(Boolean).join(" · ") || "—";
};

const dateOnly = (d) =>
  d ? new Date(d).toLocaleDateString("en-CA", { timeZone: "UTC" }) : "—";
const dateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-CA", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const BASIS_LABEL = {
  quantity: "Qty × rate",
  coverage: "Qty × coverage × rate",
  hours: "Hours × rate",
};

const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#374151",
    borderColor: s.isFocused ? ACCENT : "#4b5563",
    boxShadow: "none",
    minHeight: 34,
    fontSize: 13,
    "&:hover": { borderColor: ACCENT },
  }),
  singleValue: (p) => ({ ...p, color: "#e5e7eb" }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  placeholder: (p) => ({ ...p, color: "#9ca3af" }),
  menu: (p) => ({ ...p, backgroundColor: "#1f2937", zIndex: 80 }),
  menuPortal: (p) => ({ ...p, zIndex: 9999 }),
  option: (p, s) => ({
    ...p,
    fontSize: 13,
    backgroundColor: s.isFocused ? "#374151" : "#1f2937",
    color: s.isSelected ? ACCENT : "#e5e7eb",
  }),
};

const COVERAGE_UOM = /(^|\b)(sf|lf|sq\.? ?ft|lin\.? ?ft|square|linear)\b/i;
const UOM_SUGGESTIONS = ["LS", "EA", "SF", "LF", "HR", "DAY", "SQ", "BOARD"];

const lineAmount = (l) => {
  const rate = parseFloat(l.unitRate) || 0;
  if (l.basis === "hours") return (parseFloat(l.hours) || 0) * rate;
  const qty = parseFloat(l.quantity) || 0;
  if (l.basis === "coverage")
    return qty * (parseFloat(l.coveragePerUnit) || 1) * rate;
  return qty * rate;
};

const emptySubInfo = { businessName: "", address: "", phone: "", email: "" };

const emptyForm = {
  id: null,
  subContractor: "",
  union: "non-union",
  title: "",
  scheduledDate: "",
  subInfo: emptySubInfo,
  scopeOfWork: "",
  conditionsOfWork: "",
  paymentTerms: "",
  contractDocuments: [],
  lines: [],
};

const EMPTY_LOOKUPS = {
  items: [],
  users: [],
  unions: [],
  templates: [],
  settings: {},
};

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

let keySeq = 0;
const newKey = () => `l${++keySeq}`;

// An agreement can only be edited while it is issued, unsigned and not in payroll.
// A brand-new agreement (null) is always editable.
const isEditable = (a) =>
  !a || (a.status === "issued" && !a.signedAt && !a.payrollLocked);

const buildForm = (a, lk) => ({
  id: a._id,
  subContractor: a.subContractor?._id || a.subContractor || "",
  union: a.union || "non-union",
  title: a.title || "",
  scheduledDate: toDateInput(a.scheduledDate),
  subInfo: { ...emptySubInfo, ...(a.subInfo || {}) },
  scopeOfWork: a.scopeOfWork || "",
  conditionsOfWork: a.conditionsOfWork || lk?.settings?.conditionsOfWork || "",
  paymentTerms: a.paymentTerms || lk?.settings?.paymentTerms || "",
  contractDocuments: (a.contractDocuments || []).map((d) => ({
    key: newKey(),
    type: d.type || "",
    date: toDateInput(d.date),
  })),
  lines: (a.lineItems || []).map((l) => ({
    key: newKey(),
    itemDatabase: l.itemDatabase || null,
    itemCode: l.itemCode || "",
    description: l.description || "",
    uom: l.uom || "",
    basis: l.basis,
    quantity: l.quantity || "",
    coveragePerUnit: l.coveragePerUnit || "",
    hours: l.hours || "",
    unitRate: l.unitRate ?? "",
  })),
});

// ── Open / download an agreement PDF ─────────────────────────────────────────
// mode: "view" opens a new tab, "download" saves the file.
// Call directly from a click handler so the popup isn't blocked.
export async function openAgreementPdf(
  a,
  headers,
  signed = false,
  mode = "download",
) {
  const win = mode === "view" ? window.open("", "_blank") : null;
  try {
    const r = await axios.get(
      `${API}/subagreements3/${a._id}/${signed ? "signed-pdf" : "pdf"}`,
      { headers, responseType: "blob" },
    );
    const clean = (s) =>
      String(s || "")
        .replace(/[\\/:*?"<>|\r\n]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    let fileName =
      [
        `Subcontractor Agreement ${clean(a.agreementNumber)}`,
        clean(a.subContractor?.displayName),
        clean(a.title),
      ]
        .filter(Boolean)
        .join(" - ") +
      (signed ? " - SIGNED" : "") +
      ".pdf";
    const cd = r.headers?.["content-disposition"] || "";
    const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
    if (star) {
      try {
        fileName = decodeURIComponent(star[1]);
      } catch {
        /* keep fallback */
      }
    }

    const url = URL.createObjectURL(
      new Blob([r.data], { type: "application/pdf" }),
    );
    if (mode === "view") {
      if (win) win.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  } catch (e) {
    if (win) win.close();
    window.alert("Could not load PDF: " + (e.message || "error"));
  }
}

// ── Small presentational helpers ─────────────────────────────────────────────
// Renders scope / terms text the same way the PDF does:
//   plain line = numbered item · "- " = bullet · "** " = bold note
function FormattedLines({ text }) {
  const lines = String(text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0)
    return <p className="text-sm text-gray-500 italic">Nothing entered.</p>;
  let n = 0;
  return (
    <div className="space-y-1.5 text-sm text-gray-200 leading-relaxed">
      {lines.map((l, i) => {
        if (l.startsWith("** "))
          return (
            <p key={i} className="font-bold text-white">
              {l.slice(3)}
            </p>
          );
        if (l.startsWith("- "))
          return (
            <p key={i} className="flex gap-2 pl-10">
              <span className="text-gray-500">•</span>
              <span>{l.slice(2)}</span>
            </p>
          );
        n += 1;
        return (
          <p key={i} className="flex gap-2">
            <span className="text-orange-400 font-mono w-8 shrink-0 text-right">
              {n}.
            </span>
            <span>{l}</span>
          </p>
        );
      })}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-gray-700 bg-gray-900/40 overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-700 bg-gray-900/60">
        <h3 className="text-[11px] font-semibold text-orange-400 uppercase tracking-widest">
          {title}
        </h3>
      </div>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
        {label}
      </p>
      <div className="text-sm text-gray-200 break-words">
        {children || <span className="text-gray-600">—</span>}
      </div>
    </div>
  );
}

const TONES = {
  gray: "bg-gray-700 text-gray-200 hover:bg-gray-600",
  green: "bg-green-700 text-white hover:bg-green-600",
  darkgreen: "bg-green-800 text-green-100 hover:bg-green-700",
  blue: "bg-blue-700 text-white hover:bg-blue-600",
  orange: "bg-orange-500 text-white hover:bg-orange-400",
  red: "bg-red-900/40 text-red-300 border border-red-800/50 hover:bg-red-900/70",
};

function ActionBtn({ tone = "gray", ...props }) {
  return (
    <button
      {...props}
      className={cls(
        "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40",
        TONES[tone],
      )}
    />
  );
}

const Divider = () => <span className="w-px h-5 bg-gray-700 mx-1" />;

// ═════════════════════════════════════════════════════════════════════════════
// One modal for create / edit / view.
//   • editable (new, or issued + unsigned + not in payroll) → form
//   • otherwise → read-only document view
// Every action (PDFs, send, mark signed, reverse, complete, reopen, pay,
// delete) lives here, so the list only needs an "Open" button.
//
// Props: woId (needed to create), agreement (null = create), headers,
//        defaultUnion, onClose, onChanged
// ═════════════════════════════════════════════════════════════════════════════
export default function SubAgreementModal({
  woId,
  agreement = null,
  headers,
  defaultUnion = "non-union",
  onClose,
  onChanged,
}) {
  const [a, setA] = useState(agreement || null);
  const [ready, setReady] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [baseline, setBaseline] = useState("");
  const [lookups, setLookups] = useState(null);
  const lookupsRef = useRef(null);
  const lk = lookups || EMPTY_LOOKUPS;

  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [tplPick, setTplPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const flashTimer = useRef(null);

  // Send-for-signature
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendMsg, setSendMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState("");

  // Reverse signature
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState("");
  const [reversing, setReversing] = useState(false);
  const [reverseErr, setReverseErr] = useState("");

  const showFlash = (m) => {
    setFlash(m);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(""), 4500);
  };

  // ── Data loading ─────────────────────────────────────────────────────────
  const ensureLookups = async () => {
    if (lookupsRef.current) return lookupsRef.current;
    try {
      const r = await axios.get(`${API}/subagreements3/lookups`, { headers });
      const data = {
        items: r.data.items || [],
        users: r.data.users || [],
        unions: r.data.unions || [],
        templates: r.data.templates || [],
        settings: r.data.settings || {},
      };
      lookupsRef.current = data;
      setLookups(data);
      return data;
    } catch (e) {
      setFormErr(e.response?.data?.message || e.message);
      return null;
    }
  };

  // Store the agreement; if it's editable, (re)build the form from it
  const applyAgreement = async (full) => {
    setA(full);
    if (isEditable(full)) {
      const l = await ensureLookups();
      const f = buildForm(full, l);
      setForm(f);
      setBaseline(JSON.stringify(f));
      setFormErr("");
    }
  };

  const reload = async (id) => {
    const r = await axios.get(`${API}/subagreements3/${id || a._id}`, {
      headers,
    });
    await applyAgreement(r.data.agreement);
    return r.data.agreement;
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (agreement?._id) {
          let full = agreement;
          try {
            const r = await axios.get(
              `${API}/subagreements3/${agreement._id}`,
              { headers },
            );
            full = { ...agreement, ...r.data.agreement };
          } catch {
            /* fall back to the row we were given */
          }
          if (!active) return;
          await applyAgreement(full);
        } else {
          const l = await ensureLookups();
          if (!active) return;
          const f = {
            ...emptyForm,
            union: defaultUnion || "non-union",
            conditionsOfWork: l?.settings?.conditionsOfWork || "",
            paymentTerms: l?.settings?.paymentTerms || "",
          };
          setForm(f);
          setBaseline(JSON.stringify(f));
        }
      } finally {
        if (active) setReady(true);
      }
    })();
    return () => {
      active = false;
      clearTimeout(flashTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived state ────────────────────────────────────────────────────────
  const isNew = !a;
  const editable = isEditable(a);
  const eSigned = !!a?.signature?.name;
  const locked = !!a?.payrollLocked;

  const dirty = useMemo(
    () => editable && ready && JSON.stringify(form) !== baseline,
    [editable, ready, form, baseline],
  );

  const lockReason = !a
    ? ""
    : a.signedAt
      ? "Signed and locked — reverse the signature to edit."
      : a.payrollLocked
        ? "In payroll — locked."
        : a.status === "paid"
          ? "Paid — locked."
          : a.status === "completed"
            ? "Completed — reopen to edit."
            : "";

  const requestClose = () => {
    if (dirty && !window.confirm("Discard your unsaved changes?")) return;
    onClose();
  };

  useEffect(() => {
    const k = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (sendOpen) setSendOpen(false);
      else if (reverseOpen) setReverseOpen(false);
      else requestClose();
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });

  // ── Form helpers ─────────────────────────────────────────────────────────
  const selectedSub = useMemo(
    () => lk.users.find((u) => u._id === form.subContractor),
    [lk.users, form.subContractor],
  );
  const taxRate = selectedSub?.collectsTax ? selectedSub.taxRate || 0 : 0;

  const pickSub = (o) => {
    const id = o?.value || "";
    const u = lk.users.find((x) => x._id === id);
    setForm((f) => ({
      ...f,
      subContractor: id,
      subInfo: u?.details ? { ...emptySubInfo, ...u.details } : emptySubInfo,
    }));
  };

  const missingSubFields = useMemo(() => {
    if (!form.subContractor) return [];
    const s = form.subInfo;
    return [
      !s.businessName && "business name",
      !s.address && "address",
      !s.phone && "phone",
      !s.email && "email",
    ].filter(Boolean);
  }, [form.subContractor, form.subInfo]);

  const formTotals = useMemo(() => {
    const subtotal = form.lines.reduce((s, l) => s + lineAmount(l), 0);
    const tax = (subtotal * taxRate) / 100;
    return { subtotal, tax, total: subtotal + tax };
  }, [form.lines, taxRate]);

  const insertTemplate = (opt, mode) => {
    if (!opt) return;
    const text = opt.template.scopeText || "";
    setForm((f) => ({
      ...f,
      scopeOfWork:
        mode === "replace" || !f.scopeOfWork.trim()
          ? text
          : `${f.scopeOfWork.trim()}\n${text}`,
    }));
  };

  const addDbItem = (opt) => {
    if (!opt) return;
    const it = opt.item;
    const isCov = COVERAGE_UOM.test(it.uom || "");
    setForm((f) => ({
      ...f,
      lines: [
        ...f.lines,
        {
          key: newKey(),
          itemDatabase: it._id,
          itemCode: it.itemCode,
          description: it.description,
          uom: it.uom || "",
          basis: isCov ? "coverage" : "quantity",
          quantity: "",
          coveragePerUnit: isCov ? 1 : "",
          hours: "",
          unitRate: it.defaultUnitRate ?? 0,
        },
      ],
    }));
  };

  const addHoursLine = () =>
    setForm((f) => ({
      ...f,
      lines: [
        ...f.lines,
        {
          key: newKey(),
          itemDatabase: null,
          itemCode: "",
          description: "Labour hours",
          uom: "hr",
          basis: "hours",
          quantity: "",
          coveragePerUnit: "",
          hours: "",
          unitRate: "",
        },
      ],
    }));

  const addLumpSumLine = () =>
    setForm((f) => ({
      ...f,
      lines: [
        ...f.lines,
        {
          key: newKey(),
          itemDatabase: null,
          itemCode: "",
          description: "",
          uom: "LS",
          basis: "quantity",
          quantity: 1,
          coveragePerUnit: "",
          hours: "",
          unitRate: "",
        },
      ],
    }));

  const patchLine = (key, patch) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)),
    }));
  const removeLine = (key) =>
    setForm((f) => ({ ...f, lines: f.lines.filter((l) => l.key !== key) }));

  // Typing LS flips an hours/coverage line to qty 1 × rate (rate = lump sum)
  const changeUom = (l, raw) => {
    const value = raw.toUpperCase();
    const patch = { uom: value };
    if (value.trim().toUpperCase() === "LS" && l.basis !== "quantity") {
      patch.basis = "quantity";
      patch.quantity = 1;
    }
    patchLine(l.key, patch);
  };

  const addDoc = () =>
    setForm((f) => ({
      ...f,
      contractDocuments: [
        ...f.contractDocuments,
        { key: newKey(), type: "", date: "" },
      ],
    }));
  const patchDoc = (key, patch) =>
    setForm((f) => ({
      ...f,
      contractDocuments: f.contractDocuments.map((d) =>
        d.key === key ? { ...d, ...patch } : d,
      ),
    }));
  const removeDoc = (key) =>
    setForm((f) => ({
      ...f,
      contractDocuments: f.contractDocuments.filter((d) => d.key !== key),
    }));

  // ── Save (create or update) ──────────────────────────────────────────────
  const save = async () => {
    setFormErr("");
    if (!form.subContractor) return setFormErr("Select a subcontractor.");
    if (!form.scheduledDate) return setFormErr("Scheduled date is required.");
    if (!form.scopeOfWork.trim())
      return setFormErr("Scope of work is required.");
    if (form.lines.length === 0)
      return setFormErr("Add at least one line item.");
    if (isNew && !woId) return setFormErr("No work order selected.");

    const payload = {
      subContractor: form.subContractor,
      union: form.union,
      title: form.title,
      scheduledDate: form.scheduledDate,
      subInfo: form.subInfo,
      scopeOfWork: form.scopeOfWork,
      conditionsOfWork: form.conditionsOfWork,
      paymentTerms: form.paymentTerms,
      contractDocuments: form.contractDocuments
        .filter((d) => d.type.trim())
        .map((d) => ({ type: d.type.trim(), date: d.date || null })),
      lineItems: form.lines.map((l) => ({
        itemDatabase: l.itemDatabase,
        itemCode: l.itemCode,
        description: l.description,
        uom: l.uom,
        basis: l.basis,
        quantity: l.quantity,
        coveragePerUnit: l.coveragePerUnit,
        hours: l.hours,
        unitRate: l.unitRate,
      })),
    };

    setSaving(true);
    try {
      if (a) {
        await axios.put(`${API}/subagreements3/${a._id}`, payload, {
          headers,
        });
        await reload();
        showFlash("Changes saved.");
      } else {
        const r = await axios.post(
          `${API}/subagreements3/work-order/${woId}`,
          payload,
          { headers },
        );
        await reload(r.data.agreement._id);
        showFlash("Agreement created. You can now send it for signature.");
      }
      onChanged?.();
    } catch (e) {
      setFormErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Actions ──────────────────────────────────────────────────────────────
  // Server-side actions work on the SAVED agreement, so unsaved edits block them
  const guard = () => {
    if (dirty) {
      window.alert("You have unsaved changes. Save them first.");
      return true;
    }
    return false;
  };

  const act = async (action) => {
    if (guard()) return;
    const confirmMsg = {
      signed:
        "Mark this agreement as signed (paper copy received)? Once signed it can't be edited or deleted, and removing the signature needs an admin and a reason.",
      complete:
        "Mark this agreement completed? The cost becomes committed and it can be added to payroll.",
      reopen:
        "Reopen this agreement? It will return to issued (incurred only).",
      "mark-paid":
        "Mark as paid outside payroll? Use this only if it will NOT be added to a pay period.",
      delete: "Delete this agreement permanently?",
    }[action];
    if (confirmMsg && !window.confirm(confirmMsg)) return;

    const done = {
      signed: "Marked as signed.",
      complete: "Marked completed.",
      reopen: "Reopened.",
      "mark-paid": "Marked paid.",
    }[action];

    setBusy(true);
    try {
      if (action === "delete") {
        await axios.delete(`${API}/subagreements3/${a._id}`, { headers });
        onChanged?.();
        onClose();
        return;
      }
      if (action === "signed")
        await axios.patch(
          `${API}/subagreements3/${a._id}/signed`,
          { signed: true },
          { headers },
        );
      else
        await axios.patch(
          `${API}/subagreements3/${a._id}/${action}`,
          {},
          { headers },
        );
      await reload();
      onChanged?.();
      showFlash(done);
    } catch (e) {
      window.alert(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const pdf = async (signed, mode) => {
    if (!signed && guard()) return;
    setBusy(true);
    await openAgreementPdf(a, headers, signed, mode);
    setBusy(false);
  };

  // ── Send for signature ───────────────────────────────────────────────────
  const openSend = () => {
    if (guard()) return;
    setSendTo(a.sendInfo?.sentTo || a.subInfo?.email || "");
    setSendMsg("");
    setSendErr("");
    setSendOpen(true);
  };

  const doSend = async () => {
    setSendErr("");
    if (!/^\S+@\S+\.\S+$/.test(sendTo.trim()))
      return setSendErr("Enter a valid email address.");
    setSending(true);
    try {
      await axios.post(
        `${API}/subagreements3/${a._id}/send`,
        { to: sendTo.trim(), message: sendMsg },
        { headers },
      );
      setSendOpen(false);
      await reload();
      onChanged?.();
      showFlash(`Sent to ${sendTo.trim()}.`);
    } catch (e) {
      setSendErr(e.response?.data?.message || e.message);
    } finally {
      setSending(false);
    }
  };

  // ── Reverse signature ────────────────────────────────────────────────────
  const openReverse = () => {
    setReverseReason("");
    setReverseErr("");
    setReverseOpen(true);
  };

  const doReverse = async () => {
    setReverseErr("");
    if (reverseReason.trim().length < 5)
      return setReverseErr("Enter a reason (at least 5 characters).");
    setReversing(true);
    try {
      await axios.patch(
        `${API}/subagreements3/${a._id}/reverse-signature`,
        { reason: reverseReason.trim() },
        { headers },
      );
      setReverseOpen(false);
      await reload();
      onChanged?.();
      showFlash("Signature reversed. The agreement is editable again.");
    } catch (e) {
      setReverseErr(e.response?.data?.message || e.message);
    } finally {
      setReversing(false);
    }
  };

  // ── Select options ───────────────────────────────────────────────────────
  const itemOptions = useMemo(
    () =>
      lk.items.map((it) => ({
        value: it._id,
        label: `${it.itemCode} — ${it.description}${it.uom ? ` (${it.uom})` : ""}`,
        item: it,
      })),
    [lk.items],
  );
  const userOptions = useMemo(
    () =>
      lk.users.map((u) => ({
        value: u._id,
        label: `${u.displayName}${u.employeeNumber ? ` · #${u.employeeNumber}` : ""}`,
      })),
    [lk.users],
  );
  const unionOptions = useMemo(
    () =>
      ["non-union", ...lk.unions].map((u) => ({
        value: u,
        label: u === "non-union" ? "Non-Union" : u,
      })),
    [lk.unions],
  );
  const templateOptions = useMemo(
    () =>
      lk.templates.map((t) => ({ value: t._id, label: t.name, template: t })),
    [lk.templates],
  );
  const docTypeSuggestions = lk.settings?.documentTypes || [];

  const inputCls =
    "w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none font-mono";
  const fieldCls =
    "w-full px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none";
  const labelCls = "block text-[11px] text-gray-400 mb-1";

  const reversals = a?.signatureReversals || [];
  const viewLines = a?.lineItems || [];
  const ctx = a
    ? [projectLabel(a), woLabel(a)].filter((x) => x !== "—").join("  ·  ")
    : "";

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4"
      onMouseDown={(e) =>
        e.target === e.currentTarget && !editable && requestClose()
      }
    >
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* ── Header ── */}
        <div className="px-5 py-4 border-b border-gray-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-white font-mono">
                {isNew ? "New Subcontractor Agreement" : a.agreementNumber}
              </h2>
              {a && (
                <span
                  className={cls(
                    "px-1.5 py-0.5 text-[9px] font-semibold rounded whitespace-nowrap",
                    STATUS_STYLE[a.status],
                  )}
                >
                  {STATUS_LABEL[a.status] || a.status}
                </span>
              )}
              {a &&
                (a.signedAt ? (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-green-800/60 text-green-200">
                    🔒 Signed
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-gray-700 text-gray-400">
                    Unsigned
                  </span>
                ))}
              <span
                className={cls(
                  "px-1.5 py-0.5 text-[10px] font-semibold rounded",
                  editable
                    ? "bg-orange-500/20 text-orange-300"
                    : "bg-gray-700 text-gray-300",
                )}
              >
                {editable ? "Editing" : "Read-only"}
              </span>
              {dirty && (
                <span className="text-[10px] text-yellow-400">
                  ● unsaved changes
                </span>
              )}
            </div>
            {(a?.title || ctx) && (
              <p className="text-xs text-gray-400 mt-1">
                {a?.title}
                {a?.title && ctx ? "  —  " : ""}
                {ctx}
              </p>
            )}
          </div>
          <button
            onClick={requestClose}
            className="text-gray-500 hover:text-gray-300 text-lg leading-none px-1"
          >
            ✕
          </button>
        </div>

        {/* ── Action bar (existing agreements only) ── */}
        {a && (
          <div className="px-5 py-2.5 border-b border-gray-700 bg-gray-900/40 flex items-center gap-2 flex-wrap">
            <ActionBtn disabled={busy} onClick={() => pdf(false, "view")}>
              View PDF
            </ActionBtn>
            <ActionBtn disabled={busy} onClick={() => pdf(false, "download")}>
              ⬇ PDF
            </ActionBtn>
            {eSigned && (
              <>
                <ActionBtn
                  tone="darkgreen"
                  disabled={busy}
                  onClick={() => pdf(true, "view")}
                >
                  View signed
                </ActionBtn>
                <ActionBtn
                  disabled={busy}
                  onClick={() => pdf(true, "download")}
                >
                  ⬇ Signed
                </ActionBtn>
              </>
            )}

            <Divider />

            {!a.signedAt ? (
              <>
                <ActionBtn tone="orange" disabled={busy} onClick={openSend}>
                  {a.sendInfo?.sentAt
                    ? "Resend for signature"
                    : "Send for signature"}
                </ActionBtn>
                <ActionBtn disabled={busy} onClick={() => act("signed")}>
                  Mark signed (paper)
                </ActionBtn>
              </>
            ) : (
              <ActionBtn tone="red" disabled={busy} onClick={openReverse}>
                Reverse signature
              </ActionBtn>
            )}

            <Divider />

            {a.status === "issued" && (
              <ActionBtn
                tone="green"
                disabled={busy}
                onClick={() => act("complete")}
              >
                Complete
              </ActionBtn>
            )}
            {a.status === "completed" && !locked && (
              <>
                <ActionBtn
                  tone="blue"
                  disabled={busy}
                  onClick={() => act("mark-paid")}
                >
                  Mark paid
                </ActionBtn>
                <ActionBtn disabled={busy} onClick={() => act("reopen")}>
                  Reopen
                </ActionBtn>
              </>
            )}
            {!locked && a.status !== "paid" && !a.signedAt && (
              <ActionBtn
                tone="red"
                disabled={busy}
                onClick={() => act("delete")}
              >
                Delete
              </ActionBtn>
            )}
          </div>
        )}

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {flash && (
            <div className="rounded-lg border border-green-800/60 bg-green-950/40 px-3 py-2 text-xs text-green-300">
              ✓ {flash}
            </div>
          )}
          {!editable && lockReason && (
            <div className="rounded-lg border border-gray-600 bg-gray-900/60 px-3 py-2 text-xs text-gray-300">
              🔒 {lockReason}
            </div>
          )}

          {editable ? (
            !ready ? (
              <p className="py-16 text-center text-sm text-gray-500 animate-pulse">
                Loading…
              </p>
            ) : (
              <>
                {/* Parties + schedule */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Subcontractor</label>
                    <Select
                      options={userOptions}
                      value={
                        userOptions.find(
                          (o) => o.value === form.subContractor,
                        ) || null
                      }
                      onChange={pickSub}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      placeholder="Select…"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Labour Type</label>
                    <Select
                      options={unionOptions}
                      value={
                        unionOptions.find((o) => o.value === form.union) || null
                      }
                      onChange={(o) =>
                        setForm((f) => ({
                          ...f,
                          union: o?.value || "non-union",
                        }))
                      }
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      isSearchable={false}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>
                      Scheduled Date <span className="text-orange-400">*</span>
                    </label>
                    <input
                      type="date"
                      value={form.scheduledDate}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          scheduledDate: e.target.value,
                        }))
                      }
                      className={fieldCls}
                    />
                  </div>
                  <div className="sm:col-span-4">
                    <label className={labelCls}>Title (optional)</label>
                    <input
                      value={form.title}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, title: e.target.value }))
                      }
                      className={fieldCls}
                      placeholder="e.g. Level 3 taping"
                    />
                  </div>
                </div>

                {/* Subcontractor details */}
                {form.subContractor && (
                  <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-semibold text-orange-400 uppercase tracking-widest">
                        Subcontractor Details (prints on agreement)
                      </p>
                      {missingSubFields.length > 0 && (
                        <span className="text-[10px] text-yellow-400">
                          Not on their profile: {missingSubFields.join(", ")} —
                          fill in below
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Business Name</label>
                        <input
                          value={form.subInfo.businessName}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              subInfo: {
                                ...f.subInfo,
                                businessName: e.target.value,
                              },
                            }))
                          }
                          className={fieldCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Phone</label>
                        <input
                          value={form.subInfo.phone}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              subInfo: { ...f.subInfo, phone: e.target.value },
                            }))
                          }
                          className={fieldCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Address</label>
                        <textarea
                          rows={2}
                          value={form.subInfo.address}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              subInfo: {
                                ...f.subInfo,
                                address: e.target.value,
                              },
                            }))
                          }
                          className={fieldCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Email</label>
                        <input
                          type="email"
                          value={form.subInfo.email}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              subInfo: { ...f.subInfo, email: e.target.value },
                            }))
                          }
                          className={fieldCls}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Scope */}
                <div>
                  <div className="flex items-end justify-between gap-3 flex-wrap mb-1">
                    <label className="text-[11px] text-gray-400">
                      Scope of Work <span className="text-orange-400">*</span>
                      <span className="ml-2 text-gray-600">
                        one item per line · “- ” = bullet · “** ” = bold note
                      </span>
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="w-56">
                        <Select
                          options={templateOptions}
                          value={tplPick}
                          onChange={setTplPick}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          placeholder={
                            templateOptions.length
                              ? "Scope template…"
                              : "No templates yet"
                          }
                          isDisabled={templateOptions.length === 0}
                          isClearable
                        />
                      </div>
                      <button
                        disabled={!tplPick}
                        onClick={() => insertTemplate(tplPick, "append")}
                        className="px-2.5 py-1.5 text-xs text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600 disabled:opacity-40"
                      >
                        Add
                      </button>
                      <button
                        disabled={!tplPick}
                        onClick={() => insertTemplate(tplPick, "replace")}
                        className="px-2.5 py-1.5 text-xs text-white rounded-lg hover:brightness-110 disabled:opacity-40"
                        style={{ backgroundColor: ACCENT }}
                      >
                        Replace
                      </button>
                    </div>
                  </div>
                  <textarea
                    rows={8}
                    value={form.scopeOfWork}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, scopeOfWork: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none font-mono"
                    placeholder="Pick a scope template above, or type the scope. Items are numbered automatically on the PDF."
                  />
                </div>

                {/* Contract documents */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] text-gray-400">
                      Contract Documents{" "}
                      <span className="text-gray-600">
                        (prints as “As per the following Contract Documents”)
                      </span>
                    </label>
                    <button
                      onClick={addDoc}
                      className="px-2.5 py-1 text-xs text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600"
                    >
                      + Add document
                    </button>
                  </div>
                  {form.contractDocuments.length === 0 ? (
                    <p className="text-xs text-gray-500 italic py-3 text-center border border-dashed border-gray-700 rounded-lg">
                      None — click Add document to list tenders, drawings, etc.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <datalist id="doc-type-suggestions">
                        {docTypeSuggestions.map((t) => (
                          <option key={t} value={t} />
                        ))}
                      </datalist>
                      {form.contractDocuments.map((d) => (
                        <div key={d.key} className="flex items-center gap-2">
                          <input
                            list="doc-type-suggestions"
                            value={d.type}
                            onChange={(e) =>
                              patchDoc(d.key, { type: e.target.value })
                            }
                            placeholder="Type, e.g. Drawings, Tender"
                            className={cls(fieldCls, "flex-1")}
                          />
                          <input
                            type="date"
                            value={d.date}
                            onChange={(e) =>
                              patchDoc(d.key, { date: e.target.value })
                            }
                            title="Date issued"
                            className={cls(fieldCls, "w-44")}
                          />
                          <button
                            onClick={() => removeDoc(d.key)}
                            className="text-red-500 hover:text-red-400 px-1"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lines */}
                <div>
                  <div className="flex items-end gap-2 mb-2 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <label className={labelCls}>Add item from database</label>
                      <Select
                        options={itemOptions}
                        value={null}
                        onChange={addDbItem}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        placeholder="Search labour items…"
                      />
                    </div>
                    <button
                      onClick={addLumpSumLine}
                      className="px-3 py-2 text-xs font-medium text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600"
                    >
                      + Lump sum line
                    </button>
                    <button
                      onClick={addHoursLine}
                      className="px-3 py-2 text-xs font-medium text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600"
                    >
                      + Hours line
                    </button>
                  </div>

                  {form.lines.length === 0 ? (
                    <p className="text-xs text-gray-500 italic py-4 text-center border border-dashed border-gray-700 rounded-lg">
                      No lines yet — pick an item above, or add a lump sum /
                      hourly line.
                    </p>
                  ) : (
                    <div className="border border-gray-700 rounded-lg overflow-x-auto">
                      <datalist id="uom-suggestions">
                        {UOM_SUGGESTIONS.map((u) => (
                          <option key={u} value={u} />
                        ))}
                      </datalist>
                      <table className="w-full text-xs">
                        <thead className="bg-gray-900">
                          <tr>
                            {[
                              "Item",
                              "Basis",
                              "Qty / Hrs",
                              "UOM",
                              "Coverage",
                              "Rate",
                              "Amount",
                              "",
                            ].map((h) => (
                              <th
                                key={h}
                                className={cls(
                                  "px-2 py-2 text-gray-400 font-medium",
                                  ["Rate", "Amount"].includes(h)
                                    ? "text-right"
                                    : "text-left",
                                )}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/50">
                          {form.lines.map((l) => (
                            <tr key={l.key}>
                              <td className="px-2 py-1.5 min-w-[200px]">
                                {l.itemDatabase ? (
                                  <div>
                                    <span className="font-mono text-orange-400">
                                      {l.itemCode}
                                    </span>
                                    <span className="block text-[11px] text-gray-400">
                                      {l.description}
                                    </span>
                                  </div>
                                ) : (
                                  <input
                                    value={l.description}
                                    onChange={(e) =>
                                      patchLine(l.key, {
                                        description: e.target.value,
                                      })
                                    }
                                    placeholder="Description"
                                    className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
                                  />
                                )}
                              </td>
                              <td className="px-2 py-1.5">
                                <select
                                  value={l.basis}
                                  onChange={(e) =>
                                    patchLine(l.key, { basis: e.target.value })
                                  }
                                  className="px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
                                >
                                  <option value="quantity">Qty × rate</option>
                                  <option value="coverage">
                                    Qty × coverage × rate
                                  </option>
                                  <option value="hours">Hours × rate</option>
                                </select>
                              </td>
                              <td className="px-2 py-1.5 w-24">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.25"
                                  value={
                                    l.basis === "hours" ? l.hours : l.quantity
                                  }
                                  onChange={(e) =>
                                    patchLine(
                                      l.key,
                                      l.basis === "hours"
                                        ? { hours: e.target.value }
                                        : { quantity: e.target.value },
                                    )
                                  }
                                  className={inputCls}
                                />
                              </td>
                              <td className="px-2 py-1.5 w-20">
                                <input
                                  list="uom-suggestions"
                                  value={l.uom}
                                  onChange={(e) => changeUom(l, e.target.value)}
                                  placeholder="UOM"
                                  title="Unit of measure — type LS for lump sum"
                                  className={cls(inputCls, "uppercase")}
                                />
                              </td>
                              <td className="px-2 py-1.5 w-24">
                                {l.basis === "coverage" ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={l.coveragePerUnit}
                                    onChange={(e) =>
                                      patchLine(l.key, {
                                        coveragePerUnit: e.target.value,
                                      })
                                    }
                                    className={inputCls}
                                    title="LF / SF covered per unit"
                                  />
                                ) : (
                                  <span className="text-gray-600">—</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 w-24">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={l.unitRate}
                                  onChange={(e) =>
                                    patchLine(l.key, {
                                      unitRate: e.target.value,
                                    })
                                  }
                                  className={cls(inputCls, "text-right")}
                                />
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono text-orange-300 whitespace-nowrap">
                                ${fmt(lineAmount(l))}
                              </td>
                              <td className="px-2 py-1.5">
                                <button
                                  onClick={() => removeLine(l.key)}
                                  className="text-red-500 hover:text-red-400"
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Payment terms + conditions (copies of company templates) */}
                <div className="rounded-lg border border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowTerms((s) => !s)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left"
                  >
                    <span className="text-[11px] font-semibold text-orange-400 uppercase tracking-widest">
                      Payment Terms &amp; Conditions of Work
                    </span>
                    <span className="text-xs text-gray-500">
                      {showTerms ? "▲ Hide" : "▼ Edit for this agreement"}
                    </span>
                  </button>
                  {showTerms && (
                    <div className="px-3 pb-3 space-y-3">
                      <p className="text-[10px] text-gray-500">
                        Loaded from your Company Profile templates. Changes here
                        apply to this agreement only. One item per line; “- ”
                        makes a bullet under the previous item.
                      </p>
                      <div>
                        <label className={labelCls}>Payment Terms</label>
                        <textarea
                          rows={8}
                          value={form.paymentTerms}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              paymentTerms: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none font-mono"
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Conditions of Work</label>
                        <textarea
                          rows={14}
                          value={form.conditionsOfWork}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              conditionsOfWork: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none font-mono"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )
          ) : (
            /* ───────────────────────── READ-ONLY VIEW ───────────────────── */
            <>
              <Section title="Overview">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Field label="Project">
                    {projectLabel(a) !== "—" && projectLabel(a)}
                  </Field>
                  <Field label="Work Order">
                    {woLabel(a) !== "—" && woLabel(a)}
                  </Field>
                  <Field label="Scheduled Date">
                    {dateOnly(a.scheduledDate)}
                  </Field>
                  <Field label="Labour Type">
                    {a.union === "non-union" ? "Non-Union" : a.union}
                  </Field>
                  <Field label="Created">{dateTime(a.createdAt)}</Field>
                  <Field label="Completed">
                    {a.completedAt ? dateTime(a.completedAt) : null}
                  </Field>
                  <Field label="Paid">
                    {a.datePaid ? dateOnly(a.datePaid) : null}
                  </Field>
                  <Field label="Payroll">
                    {a.payrollLocked ? "Added to payroll" : null}
                  </Field>
                </div>
              </Section>

              <Section title="Subcontractor">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Business Name">{a.subInfo?.businessName}</Field>
                  <Field label="Contact">{a.subContractor?.displayName}</Field>
                  <Field label="Phone">{a.subInfo?.phone}</Field>
                  <Field label="Email">{a.subInfo?.email}</Field>
                  <div className="sm:col-span-2">
                    <Field label="Address">
                      {a.subInfo?.address ? (
                        <span className="whitespace-pre-line">
                          {a.subInfo.address}
                        </span>
                      ) : null}
                    </Field>
                  </div>
                </div>
              </Section>

              <Section title="Scope of Work">
                <FormattedLines text={a.scopeOfWork} />
              </Section>

              {(a.contractDocuments || []).length > 0 && (
                <Section title="Contract Documents">
                  <ul className="space-y-1 text-sm text-gray-200">
                    {a.contractDocuments.map((d, i) => (
                      <li key={i} className="flex gap-3">
                        <span>{d.type}</span>
                        <span className="text-gray-500">
                          {d.date ? dateOnly(d.date) : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              <Section title="Line Items & Pricing">
                {viewLines.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No line items.</p>
                ) : (
                  <div className="overflow-x-auto -mx-4">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-900">
                        <tr>
                          {[
                            "Item",
                            "Basis",
                            "Qty / Hrs",
                            "UOM",
                            "Coverage",
                            "Rate",
                            "Amount",
                          ].map((h) => (
                            <th
                              key={h}
                              className={cls(
                                "px-3 py-2 text-gray-400 font-medium",
                                [
                                  "Qty / Hrs",
                                  "Coverage",
                                  "Rate",
                                  "Amount",
                                ].includes(h)
                                  ? "text-right"
                                  : "text-left",
                              )}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700/50">
                        {viewLines.map((l, i) => (
                          <tr
                            key={l._id || i}
                            className={
                              i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"
                            }
                          >
                            <td className="px-3 py-2">
                              {l.itemCode && (
                                <span className="font-mono text-orange-400 mr-2">
                                  {l.itemCode}
                                </span>
                              )}
                              <span className="text-gray-200">
                                {l.description}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-400">
                              {BASIS_LABEL[l.basis] || l.basis}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-300 font-mono">
                              {l.basis === "hours" ? l.hours : l.quantity}
                            </td>
                            <td className="px-3 py-2 text-gray-400">
                              {l.uom || "—"}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-400 font-mono">
                              {l.basis === "coverage" ? l.coveragePerUnit : "—"}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-300 font-mono">
                              ${fmt(l.unitRate)}
                            </td>
                            <td className="px-3 py-2 text-right text-orange-300 font-mono font-semibold">
                              ${fmt(l.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="mt-3 ml-auto w-full sm:w-72 space-y-1 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal</span>
                    <span className="font-mono text-gray-200">
                      ${fmt(a.subtotal)}
                    </span>
                  </div>
                  {(a.taxRate || 0) > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Tax ({a.taxRate}%)</span>
                      <span className="font-mono text-gray-200">
                        ${fmt(a.tax)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1 border-t border-gray-700">
                    <span className="text-gray-300 font-semibold">Total</span>
                    <span className="font-mono text-orange-300 font-bold">
                      ${fmt(a.total)}
                    </span>
                  </div>
                </div>
              </Section>

              <Section title="Payment Terms & Conditions of Work">
                <div className="space-y-3">
                  <details className="rounded-lg border border-gray-700">
                    <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-gray-300 hover:text-white">
                      Payment Terms
                    </summary>
                    <div className="px-3 pb-3 pt-1">
                      <FormattedLines text={a.paymentTerms} />
                    </div>
                  </details>
                  <details className="rounded-lg border border-gray-700">
                    <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-gray-300 hover:text-white">
                      Conditions of Work
                    </summary>
                    <div className="px-3 pb-3 pt-1">
                      <FormattedLines text={a.conditionsOfWork} />
                    </div>
                  </details>
                </div>
              </Section>

              <Section title="Signature">
                {a.signedAt ? (
                  <div className="rounded-lg border border-green-800/60 bg-green-950/30 px-4 py-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Method">
                      {eSigned ? "Electronic signature" : "Paper copy received"}
                    </Field>
                    <Field label="Signed By">
                      {eSigned ? a.signature.name : null}
                    </Field>
                    <Field label="Signed On">{dateTime(a.signedAt)}</Field>
                    {eSigned && (
                      <div className="sm:col-span-3 text-[11px] text-gray-500 space-y-0.5">
                        {a.signature?.ip && <p>IP address: {a.signature.ip}</p>}
                        {a.signature?.userAgent && (
                          <p className="truncate" title={a.signature.userAgent}>
                            Device: {a.signature.userAgent}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-700 bg-gray-800/60 px-4 py-3 text-sm text-gray-400">
                    Not signed yet.
                    {a.sendInfo?.sentAt && (
                      <span className="block text-xs text-blue-300 mt-1">
                        Sent to {a.sendInfo.sentTo} on{" "}
                        {dateTime(a.sendInfo.sentAt)}
                      </span>
                    )}
                  </div>
                )}
              </Section>
            </>
          )}

          {/* Reversal history — shown in both modes once it exists */}
          {reversals.length > 0 && (
            <Section title={`Signature Reversal History (${reversals.length})`}>
              <div className="overflow-x-auto -mx-4">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900">
                    <tr>
                      {[
                        "Reversed",
                        "By",
                        "Was Signed By",
                        "Originally Signed",
                        "Reason",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left text-gray-400 font-medium"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {[...reversals]
                      .sort(
                        (x, y) =>
                          new Date(y.reversedAt) - new Date(x.reversedAt),
                      )
                      .map((r, i) => (
                        <tr key={r._id || i}>
                          <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                            {dateTime(r.reversedAt)}
                          </td>
                          <td className="px-3 py-2 text-gray-300">
                            {r.reversedBy?.displayName || "—"}
                          </td>
                          <td className="px-3 py-2 text-gray-400">
                            {r.signedBy ||
                              (r.method === "manual" ? "Paper copy" : "—")}
                          </td>
                          <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                            {dateTime(r.previousSignedAt)}
                          </td>
                          <td className="px-3 py-2 text-gray-200">
                            {r.reason}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-5 py-4 border-t border-gray-700 flex items-center justify-between flex-wrap gap-3">
          {editable && ready ? (
            <>
              <div className="text-xs text-gray-400 space-y-0.5">
                <div>
                  Subtotal{" "}
                  <span className="text-gray-200 font-mono">
                    ${fmt(formTotals.subtotal)}
                  </span>
                  {taxRate > 0 && (
                    <>
                      {" "}
                      · Tax ({taxRate}%){" "}
                      <span className="text-gray-200 font-mono">
                        ${fmt(formTotals.tax)}
                      </span>
                    </>
                  )}
                </div>
                <div>
                  Total{" "}
                  <span className="text-orange-300 font-mono font-semibold text-sm">
                    ${fmt(formTotals.total)}
                  </span>
                  <span className="ml-2 text-yellow-500/80">
                    · cost is incurred on save, committed when completed
                  </span>
                </div>
                {formErr && <div className="text-red-400">{formErr}</div>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={requestClose}
                  className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
                >
                  Close
                </button>
                <button
                  onClick={save}
                  disabled={saving || (!isNew && !dirty)}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 disabled:opacity-40"
                  style={{ backgroundColor: ACCENT }}
                >
                  {saving
                    ? "Saving…"
                    : isNew
                      ? "Create Agreement"
                      : "Save Changes"}
                </button>
              </div>
            </>
          ) : (
            <>
              <span />
              <button
                onClick={requestClose}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Send for signature (nested) ── */}
      {sendOpen && a && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] px-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
              <h2 className="text-base font-semibold text-white">
                Send {a.agreementNumber} for signature
              </h2>
              <button
                onClick={() => setSendOpen(false)}
                className="text-gray-500 hover:text-gray-300 text-lg leading-none"
              >
                ✕
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className={labelCls}>Subcontractor email</label>
                <input
                  type="email"
                  value={sendTo}
                  onChange={(e) => setSendTo(e.target.value)}
                  className={fieldCls}
                />
              </div>
              <div>
                <label className={labelCls}>Message (optional)</label>
                <textarea
                  rows={3}
                  value={sendMsg}
                  onChange={(e) => setSendMsg(e.target.value)}
                  className={fieldCls}
                />
              </div>
              <p className="text-[10px] text-gray-500">
                They receive a private link (valid 14 days) to review and sign.
                Resending invalidates the previous link.
              </p>
              {sendErr && <p className="text-xs text-red-400">{sendErr}</p>}
            </div>
            <div className="px-5 py-4 border-t border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setSendOpen(false)}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={doSend}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: ACCENT }}
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reverse signature (nested) ── */}
      {reverseOpen && a && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] px-4">
          <div className="bg-gray-800 border border-red-800/60 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
              <h2 className="text-base font-semibold text-white">
                Reverse signature — {a.agreementNumber}
              </h2>
              <button
                onClick={() => setReverseOpen(false)}
                className="text-gray-500 hover:text-gray-300 text-lg leading-none"
              >
                ✕
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="rounded-lg bg-red-950/40 border border-red-800/50 px-3 py-2 text-xs text-red-200 space-y-1">
                <p className="font-semibold">This removes the signed status.</p>
                <p>
                  The signature{eSigned ? ` by ${a.signature.name}` : ""} and
                  signed PDF will no longer be active, the signing link is
                  cancelled, and the agreement must be re-signed. A copy of the
                  old signature is archived with your reason.
                </p>
              </div>
              <div>
                <label className={labelCls}>
                  Reason <span className="text-orange-400">*</span>
                </label>
                <textarea
                  rows={3}
                  value={reverseReason}
                  onChange={(e) => setReverseReason(e.target.value)}
                  className={fieldCls}
                  placeholder="e.g. Scope changed after signing — re-issuing"
                />
              </div>
              {reverseErr && (
                <p className="text-xs text-red-400">{reverseErr}</p>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setReverseOpen(false)}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={doReverse}
                disabled={reversing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-700 rounded-lg hover:bg-red-600 disabled:opacity-40"
              >
                {reversing ? "Reversing…" : "Reverse Signature"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
